import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { EmailPlansManager } from '@ctct/application-layer';
import {
  isStripeBillingStrategy,
  PLAN_GROUP_IDS,
  type BillingStrategyQuery,
  type PlanTypesEnum,
} from '@ctct/domain-layer';
import type {
  ContactsTierSelectHooks,
  EmailPlansHooks,
  EmailPlansPageStore,
  PrepaySelectHooks,
} from '@ctct/state-client-layer';
import type { PaymentStrategyQuery } from '@ctct/state-server-layer';
import {
  useAddOnsFacade,
  useExtendedBillingAccount,
  useIsCanceled,
  usePaymentPlanFacade,
  usePlanDetail,
  usePlanGroupFacade,
  useIsLegacyPlanGroup,
  useShouldDisplayModernPayflowForLegacyPlans,
  useUpgradePlanPromotion,
  useUserRole,
  useHasScheduledDowngrade,
  useSuspensionInfoFacade,
  useIsStripeScheduledDowngradeEnabled,
  useHasStripeOverdue,
  useHasCharmPricing,
  PaymentStrategy,
} from '@ctct/state-server-layer';

import { usePlanIdForSmsBundleAddOns } from '../../hooks/usePlanIdForSmsBundleAddOns';
import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { useIsCoreMutationLockedAccountState } from '../helpers/useIsSingleBillChildAccount';

type Flags = {
  isMWebVariantEnabled?: boolean;
  isValueFocusedMWebPlanSelectionEnabled?: boolean;
  isSmsBundle?: boolean;
  shouldIncludeNon3plPlans?: boolean;
  shouldUseModernPayflowForLegacyPlans?: boolean;
};

interface Props {
  initializer: Pick<EmailPlansManager, 'emailPlansInit'>;
  pageStore: EmailPlansPageStore;
  hooks: EmailPlansHooks;
  initialPlanType?: PlanTypesEnum;
  billingStrategy: BillingStrategyQuery;
  paymentStrategy: PaymentStrategyQuery;
  externalHooks: {
    useContactsTier: ContactsTierSelectHooks['useContactsTier'];
    usePrepay: PrepaySelectHooks['usePrepay'];
    useSmsBundleTier: () => string | null;
  };
  flags?: Flags;
}

export const defaultUseContactsTier = () => '0';

export const defaultUsePrepay = () => '1';

export const defaultUseSmsBundleTier = (): string | null => null;

export const useEmailPlansWidget = ({
  initializer,
  pageStore,
  hooks,
  initialPlanType,
  externalHooks,
  billingStrategy,
  paymentStrategy,
  flags = {},
}: Props) => {
  const { t } = useTranslation();

  const isCanceled = useIsCanceled();
  const hasCharmPricing = useHasCharmPricing();
  const extendedBillingAccountQuery = useExtendedBillingAccount();
  // Derive microEmail3PlGroupId before calling usePlanGroupFacade so we can pass it as the
  // override. This keeps usePlanGroupFacade lightweight (no usePlanDetail inside it), avoiding
  // extra re-renders in every other component that also calls usePlanGroupFacade.
  const planDetailQuery = usePlanDetail();
  const isMicroEmail = planDetailQuery.data?.isMicroEmail() ?? false;
  const isPlanRestructureExperiment = planDetailQuery.data?.isPlanRestructureExperiment() ?? false;
  const isStripeUser = isStripeBillingStrategy(billingStrategy);
  const { isLegacyPlanGroup = false } = useIsLegacyPlanGroup();
  const shouldFetch3PlPromotion =
    planDetailQuery.data?.shouldFetch3PlPromotion(isStripeUser, isLegacyPlanGroup) ?? false;
  const promotionQuery = useUpgradePlanPromotion(shouldFetch3PlPromotion);
  const microEmail3PlGroupId = shouldFetch3PlPromotion
    ? promotionQuery.data?.getRecommended3PlPlanGroupId() ?? PLAN_GROUP_IDS.V21GBB_PLANGROUP_ID
    : undefined;
  // Use isPending (not isLoading) so the gate stays active during the brief window where
  // the query's fetchStatus is still 'idle' (fetch scheduled but not yet started). isLoading
  // = isPending && isFetching, which can be false during that window, causing isPromotionPending
  // to prematurely flip to false and onMount to fire before microEmail3PlGroupId resolves.
  const isPromotionPending = shouldFetch3PlPromotion && promotionQuery.isPending;
  const planGroupQuery = usePlanGroupFacade(microEmail3PlGroupId);

  const paymentPlanQuery = usePaymentPlanFacade(isStripeUser);
  const userRoleQuery = useUserRole();
  const hasStripeOverdueQuery = useHasStripeOverdue(isStripeUser);
  const { data: isStripeScheduledDowngradeEnabled } = useIsStripeScheduledDowngradeEnabled();
  const hasScheduledDowngradeQuery = useHasScheduledDowngrade(
    isStripeUser && Boolean(isStripeScheduledDowngradeEnabled)
  );
  const suspensionInfoQuery = useSuspensionInfoFacade();

  const { isMWebVariantEnabled, isValueFocusedMWebPlanSelectionEnabled, isSmsBundle = false } = flags;

  const selectedEmailPlanId = pageStore((state) => state.selectedEmailPlanId);

  const planIdForAddOnsRequest = usePlanIdForSmsBundleAddOns({ selectedEmailPlanId, isSmsBundle });

  // For paid Micro Email users, Plans & Pricing shows group 109 plans (not the user's own
  // group 379). The add-ons endpoint must use the same overridden group so the plan group
  // and plan ID match. microEmail3PlGroupId is undefined for all non-Micro-Email users.
  const addOnsQuery = useAddOnsFacade(planIdForAddOnsRequest, true, microEmail3PlGroupId);

  const { isLocked: isCoreMutationLockedAccount, isPending: isCoreMutationLockPending } =
    useIsCoreMutationLockedAccountState({ billingStrategy });
  const isConfirmedCoreMutationLockedAccount = Boolean(isCoreMutationLockedAccount && !isCoreMutationLockPending);

  const shouldIncludeNon3plPlans = paymentStrategy.data === PaymentStrategy.Stripe;

  // Check if the modern_payflow_for_legacy_plans Statsig flag is enabled
  const { data: shouldUseModernPayflowForLegacyPlans } = useShouldDisplayModernPayflowForLegacyPlans();

  const componentProps = useMemo(() => {
    return {
      ...initializer.emailPlansInit({
        pageStore,
        queries: [
          planGroupQuery,
          planDetailQuery,
          paymentPlanQuery,
          extendedBillingAccountQuery,
          userRoleQuery,
          hasScheduledDowngradeQuery,
          suspensionInfoQuery,
          addOnsQuery,
        ],
        t,
        initialPlanType,
        flags: {
          isCanceled,
          hasStripeOverdue: Boolean(hasStripeOverdueQuery.data),
          isMWebVariantEnabled,
          hasCharmPricing,
          isValueFocusedMWebPlanSelectionEnabled,
          isSmsBundle,
          isSingleBillChildAccount: isConfirmedCoreMutationLockedAccount,
          isCoreMutationLockPending: Boolean(isCoreMutationLockPending),
          isMicroEmail,
          microEmail3PlGroupId,
          isPromotionPending,
          isPlanRestructureExperiment,
          shouldIncludeNon3plPlans,
          shouldUseModernPayflowForLegacyPlans: shouldUseModernPayflowForLegacyPlans ?? false,
        },
      }),
      hooks,
      externalHooks,
    };
  }, [
    initializer,
    pageStore,
    planGroupQuery,
    planDetailQuery,
    paymentPlanQuery,
    extendedBillingAccountQuery,
    userRoleQuery,
    hasScheduledDowngradeQuery,
    suspensionInfoQuery,
    addOnsQuery,
    hasStripeOverdueQuery,
    isCoreMutationLockedAccount,
    isCoreMutationLockPending,
    isConfirmedCoreMutationLockedAccount,
    t,
    initialPlanType,
    isCanceled,
    isMWebVariantEnabled,
    isValueFocusedMWebPlanSelectionEnabled,
    isSmsBundle,
    isMicroEmail,
    microEmail3PlGroupId,
    shouldFetch3PlPromotion,
    promotionQuery,
    isPromotionPending,
    isPlanRestructureExperiment,
    hooks,
    externalHooks,
    hasCharmPricing,
    shouldIncludeNon3plPlans,
    shouldUseModernPayflowForLegacyPlans,
  ]);

  useWidgetLifecycle(componentProps.status, componentProps.lifecycle, isCoreMutationLockPending);

  return componentProps;
};

export type EmailPlansWidgetProps = ReturnType<typeof useEmailPlansWidget>;
